import BuildIcon from "@mui/icons-material/Build";
import * as React from "react";

import type { AgentThreadToolUseContent } from "@/shared/domain/agentThread/AgentThreadRecord";
import type { ClientTool } from "@/shared/domain/agentThread/clientTools";
import { confirmationContentFor } from "@/shared/domain/agentThread/clientTools";
import { Button } from "@/shared/foundation/primitives";

import styles from "./ToolApprovalCard.module.css";
import { ToolApprovalDetailsModal } from "./ToolApprovalDetailsModal";

interface ToolApprovalCardProps {
  toolUse: AgentThreadToolUseContent;
  tool: ClientTool;
  onApprove: () => void;
  onDecline: () => void;
}

/**
 * Inline approval card rendered in the thread message stream when a
 * client-side tool requires user confirmation before executing.
 *
 * The tool's `ConfirmationContent` preview renders **here**, inline, not only
 * behind "More details". This card is the default and lowest-friction
 * approval path, so the facts a user needs to judge the request — what is
 * being created, in which collection, with which members — have to be visible
 * before Approve is clicked, not behind an optional disclosure. Tools with no
 * preview, and proposals a tool's guard rejects, fall back to the raw JSON:
 * Approve is never offered over a card that says only what tool wants to run.
 * "More details" remains for the roomier modal view.
 */
export function ToolApprovalCard({
  toolUse,
  tool,
  onApprove,
  onDecline,
}: ToolApprovalCardProps) {
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  // Latched once the user decides, and passed down to the details modal.
  // `approve()` is async and the card stays mounted until the runner advances,
  // so every control that could take a second decision has to go quiet — an
  // enabled button whose handler returns early is a promise the UI doesn't keep.
  const [resolved, setResolved] = React.useState(false);
  const cardRef = React.useRef<HTMLDivElement>(null);

  // Scroll the card into view when it first appears. Use block: "start"
  // so the card appears at the top of the viewport, above the scroll-down
  // button which sits at the bottom of the scroll container.
  React.useEffect(() => {
    cardRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, []);

  const params = toolUse.input ?? {};
  const Preview = confirmationContentFor(tool, params);
  // Keyed off the same call as the inline preview, so "More details" always
  // opens a roomier view of what the card is already showing. Keyed off
  // `tool.ConfirmationContent` instead, it offered a modal that either repeated
  // the card verbatim or was the only place the proposal appeared at all.
  const hasDetails = Preview !== undefined;

  const handleApprove = () => {
    if (resolved) {
      return;
    }
    setResolved(true);
    onApprove();
  };

  const handleDecline = () => {
    if (resolved) {
      return;
    }
    setResolved(true);
    onDecline();
  };

  return (
    <div className={styles.container} ref={cardRef}>
      <div className={styles.card}>
        <div className={styles.header}>
          <BuildIcon className={styles.titleIcon} />
          <span className={styles.titleText}>
            <b>{tool.displayName}</b> wants to run
          </span>
        </div>

        <div className={styles.preview}>
          {Preview ? (
            <Preview params={params} />
          ) : (
            // No rich preview means the tool has none, or its guard rejected
            // these params. Either way the raw proposal goes here rather than
            // nowhere: a card that reads only "Create Collection wants to run"
            // asks the user to authorize a durable write sight unseen, and the
            // malformed case is the one where seeing it matters most.
            <pre className={styles.jsonPreview}>
              {JSON.stringify(params, null, 2)}
            </pre>
          )}
        </div>

        {hasDetails && (
          <button
            type="button"
            className={styles.detailsLink}
            onClick={() => setDetailsOpen(true)}
            disabled={resolved}
          >
            More details
          </button>
        )}

        <div className={styles.actions}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDecline}
            disabled={resolved}
          >
            Decline
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleApprove}
            disabled={resolved}
          >
            Approve
          </Button>
        </div>
      </div>

      {hasDetails && (
        <ToolApprovalDetailsModal
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          toolUse={toolUse}
          tool={tool}
          resolved={resolved}
          onApprove={handleApprove}
          onDecline={handleDecline}
        />
      )}
    </div>
  );
}
